// src/lib/supabaseQueries.js
// Centralized Supabase queries for The Fade

import { supabase } from './supabase';

// =====================================================
// USER QUERIES
// =====================================================

export const getCurrentUser = async () => {
  // Check for active session first
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError) {
    console.error('Session error:', sessionError);
    throw sessionError;
  }
  
  if (!session) {
    throw new Error('No active session - please log in');
  }
  
  // Get full user profile from users table
  const { data: profile, error: profileError } = await supabase
    .from('users')
    .select('*')
    .eq('id', session.user.id)
    .single();
  
  if (profileError) {
    console.error('Profile error:', profileError);
    throw profileError;
  }
  
  return profile;
};


// =====================================================
// SEASON QUERIES
// =====================================================

// SIMPLER VERSION - REPLACE getUserSeasons IN src/lib/supabaseQueries.js

export const getUserSeasons = async (userId) => {
  try {
    const { data: memberships, error: memberError } = await supabase
      .from('season_members')
      .select('season_id')
      .eq('user_id', userId);

    if (memberError) throw memberError;
    if (!memberships || memberships.length === 0) return [];

    const seasonIds = memberships.map(m => m.season_id);

    // Fetch seasons WITH commissioner username
    const { data: seasons, error: seasonsError } = await supabase
      .from('seasons')
      .select(`
        id,
        name,
        start_date,
        end_date,
        commissioner_id,
        scoring_type,
        invite_code,
        users!seasons_commissioner_id_fkey (
          username
        )
      `)
      .in('id', seasonIds);

    if (seasonsError) throw seasonsError;

    // Format and count members
    const seasonsWithData = await Promise.all(
      seasons.map(async (season) => {
        // 1. Get member count (Your existing logic)
        const { count } = await supabase
          .from('season_members')
          .select('*', { count: 'exact', head: true })
          .eq('season_id', season.id);

        // 2. Calculate current week for H2H logic
        const start = new Date(season.start_date);
        const now = new Date();
        const diffWeeks = Math.ceil(Math.abs(now - start) / (1000 * 60 * 60 * 24 * 7));
        const currentWeekNum = Math.max(1, diffWeeks);

        // 3. Fetch Matchup for this specific user in this specific week
        const { data: matchup } = await supabase
          .from('matchups')
          .select(`
            id,
            user1_id,
            user2_id,
            user1_score,
            user2_score,
            user1:users!user1_id(username),
            user2:users!user2_id(username)
          `)
          .eq('season_id', season.id)
          .eq('week_number', currentWeekNum)
          .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
          .maybeSingle();

        // Determine opponent name and scores
        let h2hData = null;
        if (matchup) {
          const isUser1 = matchup.user1_id === userId;
          h2hData = {
            opponentName: isUser1 ? (matchup.user2?.username || 'Unknown') : (matchup.user1?.username || 'Unknown'),
            myScore: isUser1 ? matchup.user1_score : matchup.user2_score,
            opponentScore: isUser1 ? matchup.user2_score : matchup.user1_score,
            week: currentWeekNum
          };
        }

        return {
          id: season.id,
          name: season.name,
          start_date: season.start_date,
          end_date: season.end_date,
startDate: new Date(season.start_date).toLocaleDateString('en-US', { 
  month: 'short', 
  day: 'numeric',
  timeZone: 'America/New_York'  // ← ADD THIS
}),
endDate: new Date(season.end_date).toLocaleDateString('en-US', { 
  month: 'short', 
  day: 'numeric',
  timeZone: 'America/New_York'  // ← ADD THIS
}),
          commissioner_id: season.commissioner_id,
          commissioner: season.users?.username || 'Unknown',
          members: count || 0,
          scoringType: season.scoring_type,
          invite_code: season.invite_code,
          isActive: true,
          // NEW H2H FIELD ADDED BELOW
          currentMatchup: h2hData 
        };
      })
    );

    return seasonsWithData;
  } catch (error) {
    console.error('Error in getUserSeasons:', error);
    return [];
  }
};

export const createSeason = async (seasonData, userId) => {
  // Generate invite code
  const { data: inviteCode } = await supabase.rpc('generate_invite_code');
  
  const { data, error } = await supabase
    .from('seasons')
    .insert([{
      name: seasonData.name,
      commissioner_id: userId,
      start_date: seasonData.startDate,
      end_date: seasonData.endDate,
      scoring_type: seasonData.scoringType,
      pick_frequency: seasonData.pickFrequency,
      starting_bankroll: seasonData.startingBankroll,
      sports: seasonData.sports,
      is_private_money_pool: seasonData.isPrivateMoneyPool,
      invite_code: inviteCode
    }])
    .select()
    .single();
  
  if (error) throw error;
  
  // Add commissioner as first member
  await supabase
    .from('season_members')
    .insert([{
      season_id: data.id,
      user_id: userId,
      bankroll: seasonData.startingBankroll
    }]);
  
  return data;
};

export const joinSeason = async (inviteCode, userId) => {
  // Find season by invite code
  const { data: season, error: seasonError } = await supabase
    .from('seasons')
    .select('*')
    .eq('invite_code', inviteCode)
    .single();
  
  if (seasonError) throw seasonError;
  
  // Add user as member
  const { data, error } = await supabase
    .from('season_members')
    .insert([{
      season_id: season.id,
      user_id: userId,
      bankroll: season.starting_bankroll
    }])
    .select()
    .single();
  
  if (error) throw error;
  return season;
};

// =====================================================
// GAME QUERIES
// =====================================================

export const getSeasonGames = async (seasonId, weekNumber) => {
  const { data, error } = await supabase
    .from('games')
    .select(`*, featured_players(*)`)
    .eq('season_id', seasonId)
    .eq('week_number', weekNumber) // Simple and fast
    .order('game_time', { ascending: true });
  
  if (error) throw error;
  return data;
};

export const addGame = async (gameData, seasonId, weekNumber) => {
  const { data, error } = await supabase
    .from('games')
    .insert([{
      season_id: seasonId,
      week_number: weekNumber, // Save the week explicitly
      sport: gameData.sport,
      home_team: gameData.home_team,
      away_team: gameData.away_team,
      game_time: new Date(`${gameData.gameDate}T${gameData.gameTime}`).toISOString()
    }])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const addFeaturedPlayers = async (gameId, players) => {
  const { data, error } = await supabase
    .from('featured_players')
    .insert(
      players.map(player => ({
        game_id: gameId,
        name: player.name,
        team: player.team,
        sport: player.sport,
        position: player.position
      }))
    )
    .select();
  
  if (error) throw error;
  return data;
};

// =====================================================
// PICK QUERIES
// =====================================================

// UPDATE getUserPicks IN src/lib/supabaseQueries.js
// This will fetch the game and player info so you can display them properly

export const getUserPicks = async (userId, seasonId, weekNumber) => {
  try {
    const { data, error } = await supabase
      .from('picks')
      .select(`
        *,
        games (
          id,
          sport,
          home_team,
          away_team,
          game_time,
          status,
          winner,
          final_score
        ),
        featured_players (
          id,
          name,
          team,
          sport,
          position,
          game:games (
            id,
            sport,
            home_team,
            away_team,
            game_time,
            status,
            winner,
            final_score
          )
        )
      `)
      .eq('user_id', userId)
      .eq('season_id', seasonId)
      .eq('week_number', weekNumber)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const formattedPicks = data?.map(pick => ({
      id: pick.id,
      pick_type: pick.pick_type,
      selection: pick.selection,
      status: pick.status,
      locked: pick.locked,
      is_boosted: pick.is_boosted,
      fantasy_score: pick.fantasy_score,
      performance_score: pick.performance_score,
      game: pick.games || pick.featured_players?.game, // team pick uses pick.games, player pick uses pick.featured_players.game
      player: pick.featured_players,
      created_at: pick.created_at
    })) || [];

    return formattedPicks;
  } catch (error) {
    console.error('Error in getUserPicks:', error);
    throw error;
  }
};


export const submitPicks = async (userId, seasonId, weekNumber, selections, allGames) => {
  const now = new Date();

  // 1️⃣ Identify open games (not started yet)
  const openGameIds = allGames
    .filter(g => new Date(g.game_time) > now)
    .map(g => String(g.id));

  if (!openGameIds.length) {
    throw new Error("No games are open for picking.");
  }

  // 2️⃣ Delete old picks only for open games
  const { error: deleteError } = await supabase
    .from("picks")
    .delete()
    .eq("user_id", String(userId))
    .eq("season_id", String(seasonId))
    .eq("week_number", weekNumber)
    .in("game_id", openGameIds);

  if (deleteError) {
    console.error("Delete Error:", deleteError);
    throw new Error("Could not clear old picks.");
  }

  // 3️⃣ Prepare picks to insert, only for open games
  const picksToInsert = [];

  selections.teamWinners.forEach((pick) => {
    if (!openGameIds.includes(String(pick.gameId))) return;

    picksToInsert.push({
      user_id: String(userId),
      season_id: String(seasonId),
      week_number: weekNumber,
      pick_type: "team_winner",
      game_id: String(pick.gameId),
      player_id: null,
      selection: pick.team,
      status: "pending",
      is_boosted: selections.boostedTeamId === `${pick.gameId}-${pick.team}`,
    });
  });

  selections.topPerformers.forEach((pick) => {
    if (!openGameIds.includes(String(pick.gameId))) return;

    picksToInsert.push({
      user_id: String(userId),
      season_id: String(seasonId),
      week_number: weekNumber,
      pick_type: "top_performer",
      game_id: String(pick.gameId),
      player_id: String(pick.playerId),
      selection: pick.name,
      status: "pending",
      is_boosted: selections.boostedPlayerId === pick.playerId,
    });
  });

  if (!picksToInsert.length) throw new Error("No valid picks to submit (all games may have started).");

  // 4️⃣ Insert new picks
  const { data, error: insertError } = await supabase
    .from("picks")
    .insert(picksToInsert)
    .select();

  if (insertError) {
    console.error("Insert Error:", insertError);
    throw insertError;
  }

  return data;
};








// Fetches unique selections for the week so the commish only grades each once
export const getUniquePicksToGrade = async (seasonId, week) => {
  const { data, error } = await supabase
    .from("picks")
    .select(`
      selection,
      pick_type,
      game_id,
      games (
        id,
        sport,
        home_team,
        away_team,
        game_time,
        status
      )
    `)
    .eq("season_id", seasonId)
    .eq("week_number", week)
    .eq("status", "pending");

  if (error) throw error;

  // Group by selection name to create a unique list
  const unique = data.reduce((acc, curr) => {
    if (!acc[curr.selection]) {
      acc[curr.selection] = {
        selection: curr.selection,
        pickType: curr.pick_type,
        displaySubtitle: curr.games ? `${curr.games.away_team} @ ${curr.games.home_team}` : curr.game_id,
        game: curr.games, // <-- attach full game object
        count: 0
      };
    }
    acc[curr.selection].count++;
    return acc;
  }, {});

  return Object.values(unique);
};


// Updates EVERY user who picked this specific team/player
export const gradeSelectionBulk = async (seasonId, week, selection, score) => {
  try {
    const scoreInt = parseInt(score);

    // 1. Fetch only the IDs and UserIDs we need to update
    const { data: picks, error: fetchError } = await supabase
      .from('picks')
      .select('id, user_id, is_boosted')
      .eq('season_id', seasonId)
      .eq('week_number', week)
      .eq('selection', selection)
      .eq('status', 'pending');

    if (fetchError) throw fetchError;
    if (!picks || picks.length === 0) return;

    // 2. Perform updates individually or in parallel updates
    // Using .update().eq() instead of .upsert() avoids triggering INSERT RLS policies
    await Promise.all(
      picks.map(pick => {
        const finalScore = pick.is_boosted ? scoreInt * 2 : scoreInt;
        return supabase
          .from('picks')
          .update({
            status: 'graded',
            fantasy_score: finalScore
          })
          .eq('id', pick.id);
      })
    );

    // 3. TRIGGER MATCHUP UPDATES
    const uniqueUserIds = [...new Set(picks.map(p => p.user_id))];
    await Promise.all(
      uniqueUserIds.map(userId => 
        updateMatchupScore(userId, seasonId, week)
      )
    );

    return { success: true, count: picks.length };
  } catch (error) {
    console.error('Error in bulk grading:', error);
    throw error;
  }
};

export const getAllPicksToGrade = async (seasonId, weekNumber) => {
  try {
    const { data, error } = await supabase
      .from('picks')
      .select(`
        id,
        user_id,
        pick_type,
        selection,
        status,
        locked,
        game_id,
        player_id,
        users!picks_user_id_fkey (
          username
        ),
        games (
          home_team,
          away_team,
          sport
        ),
        featured_players (
          name,
          team,
          sport
        )
      `)
      .eq('season_id', seasonId)
      .eq('week_number', weekNumber)
      .eq('status', 'pending')
      .eq('locked', true);
    
    if (error) throw error;

    console.log('📦 Raw picks from DB:', data);

    // Format the data to match what the Grade Picks modal expects
    const formatted = data?.map(pick => ({
      id: pick.id,
      user: pick.users?.username || 'Unknown User',
      pickType: pick.pick_type,  // This is what the modal uses
      pick: pick.selection,
      game: pick.pick_type === 'team_winner'
        ? (pick.games ? `${pick.games.away_team} @ ${pick.games.home_team}` : 'Unknown Game')
        : (pick.featured_players ? pick.featured_players.name : pick.selection),
      status: pick.status
    })) || [];

    console.log('✅ Formatted picks for modal:', formatted);

    return formatted;
  } catch (error) {
    console.error('Error in getAllPicksToGrade:', error);
    return [];
  }
};

export const gradePick = async (pickId, score) => {
  try {
    const scoreInt = parseInt(score);

    // 1. Fetch the pick first to see if it was boosted
    const { data: pick, error: fetchError } = await supabase
      .from('picks')
      .select('is_boosted, user_id, season_id, week_number')
      .eq('id', pickId)
      .single();

    if (fetchError) throw fetchError;

    // 2. Calculate final score (Double it if is_boosted is true)
    const finalScore = pick.is_boosted ? scoreInt * 2 : scoreInt;

    // 3. Update the pick with the calculated score
    const { data, error: updateError } = await supabase
      .from('picks')
      .update({ 
        status: 'graded',
        fantasy_score: finalScore
      })
      .eq('id', pickId)
      .select();

    if (updateError) throw updateError;

    // 4. TRIGGER MATCHUP UPDATE
    // After grading a pick, we should sync the H2H matchup table
    await updateMatchupScore(pick.user_id, pick.season_id, pick.week_number);

    return data;
  } catch (error) {
    console.error('Error grading pick:', error);
    throw error;
  }
};

export const updateMatchupScore = async (userId, seasonId, week) => {
  try {
    const { data: matchup, error: mError } = await supabase
      .from('matchups')
      .select('*')
      .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
      .eq('season_id', seasonId)
      .eq('week_number', week)
      .single();

    if (mError || !matchup) return;

    const { data: allPicks } = await supabase
      .from('picks')
      .select('user_id, fantasy_score, status')
      .eq('season_id', seasonId)
      .eq('week_number', week)
      .in('user_id', [matchup.user1_id, matchup.user2_id]);

    // Calculate totals for both users
    const u1Score = allPicks.filter(p => p.user_id === matchup.user1_id).reduce((s, p) => s + (p.fantasy_score || 0), 0);
    const u2Score = allPicks.filter(p => p.user_id === matchup.user2_id).reduce((s, p) => s + (p.fantasy_score || 0), 0);

    // Check if grading is done
    const isFinished = allPicks.length > 0 && !allPicks.some(p => p.status === 'pending');
    
    let winnerId = null;
    if (isFinished) {
      if (u1Score > u2Score) winnerId = matchup.user1_id;
      else if (u2Score > u1Score) winnerId = matchup.user2_id;
    }

    await supabase
      .from('matchups')
      .update({
        user1_score: u1Score,
        user2_score: u2Score,
        winner_id: winnerId,
        is_completed: isFinished
      })
      .eq('id', matchup.id);

  } catch (error) {
    console.error('Matchup sync error:', error);
  }
};
// =====================================================
// LEADERBOARD QUERIES
// =====================================================

export const getSeasonLeaderboard = async (seasonId) => {
  try {
    const { data: members, error } = await supabase
      .from('season_members')
      .select(`
        user_id,
        bankroll,
        users!season_members_user_id_fkey (
          username
        )
      `)
      .eq('season_id', seasonId);

    if (error) throw error;

    // Calculate stats for each member
const leaderboardData = await Promise.all(
  members.map(async (member) => {
    // 1. Fetch Picks (as you were doing)
    const { data: picks } = await supabase
      .from('picks')
      .select('status, fantasy_score')
      .eq('season_id', seasonId)
      .eq('user_id', member.user_id)
      .neq('status', 'pending');

    // 2. NEW: Fetch Wins from Matchups table
    const { data: matchupsWon } = await supabase
      .from('matchups')
      .select('id')
      .eq('season_id', seasonId)
      .eq('winner_id', member.user_id)
      .eq('is_completed', true);

    const matchWinBonus = (matchupsWon?.length || 0) * 100;

    // Calculate pick stats
    const wins = picks?.filter(p => (p.fantasy_score || 0) >= 120).length || 0;
    const losses = picks?.filter(p => (p.fantasy_score || 0) < 120).length || 0;
    const total = wins + losses;
    
    // Sum pick scores + matchup bonuses
    const pickPoints = picks?.reduce((sum, p) => sum + (p.fantasy_score || 0), 0) || 0;
    const totalPoints = pickPoints + matchWinBonus;
    
    const winPct = total > 0 ? (wins / total) * 100 : 0;
    const roi = total > 0 ? ((wins - losses) / total) * 100 : 0;

    return {
      username: member.users?.username || 'Unknown',
      wins,
      losses,
      matchWins: matchupsWon?.length || 0, // Helpful for debugging
      fantasy_score: totalPoints, 
      winPct: winPct.toFixed(1),
      roi: roi.toFixed(1),
      bankroll: member.bankroll || 0,
      totalPoints,
      rank: 0 
    };
  })
);
// 4. Don't forget to sort them so 'rank' makes sense later!
leaderboardData.sort((a, b) => b.fantasy_score - a.fantasy_score);

    // Add ranks
    leaderboardData.forEach((entry, index) => {
      entry.rank = index + 1;
    });

    return leaderboardData;
  } catch (error) {
    console.error('Error getting leaderboard:', error);
    return [];
  }
};


export const getSeasonMembers = async (seasonId) => {
  const { data, error } = await supabase
    .from('season_members')
    .select(`
      *,
      user:users(username, email)
    `)
    .eq('season_id', seasonId);
  
  if (error) throw error;
  return data;
};
